import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ClipboardList, QrCode } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import type { XkEvent, XkEventStockStation, XkStation } from '@/lib/api/types';
import { allocationsApi } from '@/features/stock/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { usePagination } from '@/lib/hooks/usePagination';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { StationQrDialog } from './components/StationQrDialog';
import { TagFilter } from '@/components/ui/TagFilter';
import { EntityTags } from '@/features/tags/EntityTags';
import { useTagAssignments, useTagMatch, useTags } from '@/lib/api/tags';

export function StationListPage() {
  // QR opens in place — the standalone /stations/:id/qr page is gone.
  const [qrStation, setQrStation] = useState<{ id: number; name: string } | null>(null);
  const t = useT();
  const brandId = useSelectedBrandId();
  useFavoritable(t('stations.title'));
  const { data, isLoading } = useQuery({
    queryKey: brandId
      ? queryKeys.stations.byBrand(brandId)
      : queryKeys.stations.all,
    queryFn: () =>
      api.list<XkStation>(
        '/xk-station',
        brandId ? { filter: { companyId: brandId } } : undefined,
      ),
  });
  /*
    Asignado por estación (CH-13). La estación no asigna: eso pasa en Plan del
    proyecto, arrastrando premios. Aquí sólo se lee cuánto lleva cada una y se
    enlaza a donde se cambia. Una petición para toda la lista, no una por fila.
  */
  const allocQ = useQuery({
    queryKey: queryKeys.stockAllocations.byBrand(brandId ?? 0),
    queryFn: () => allocationsApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId }, perPage: 500 }),
    enabled: !!brandId,
  });
  const asignado = useMemo(() => {
    const out: Record<number, { units: number; items: number }> = {};
    for (const a of (allocQ.data?.items ?? []) as XkEventStockStation[]) {
      if ((a.status as string) === 'Deleted') continue;
      const row = (out[a.stationId] ??= { units: 0, items: 0 });
      row.units += a.quantity ?? 0;
      row.items += 1;
    }
    return out;
  }, [allocQ.data]);
  const eventoDe = useMemo(() => {
    const out: Record<number, XkEvent> = {};
    for (const e of eventsQ.data?.items ?? []) out[e.id] = e;
    return out;
  }, [eventsQ.data]);

  /*
    Etiquetas.

    El vocabulario y las asignaciones se piden UNA vez para toda la lista y se
    reparten por fila: si cada fila preguntara por su cuenta, una marca con cien
    estaciones haría cien peticiones y las píldoras aparecerían a goteo.
  */
  const [filtro, setFiltro] = useState<number[]>([]);
  const vocabQ = useTags(brandId, 'station');
  const vocabulario = vocabQ.data ?? [];

  const todas = data?.items;
  const { ids: casan } = useTagMatch(brandId, 'station', filtro);

  /*
    `casan` es `null` cuando no hay filtro puesto, y eso NO es lo mismo que una
    lista vacía: `null` significa «no filtres» y `[]` significa «nada cumple».
    Confundirlos deja la pantalla en blanco nada más entrar.
  */
  const allItems = useMemo(
    () => (casan == null ? todas : (todas ?? []).filter((s) => casan.includes(s.id))),
    [todas, casan],
  );

  const asignQ = useTagAssignments(
    brandId,
    'station',
    useMemo(() => (allItems ?? []).map((s) => s.id), [allItems]),
  );
  const asignaciones = asignQ.data ?? {};

  const pager = usePagination(allItems, 12);

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('stations.title')}
            <HelpHint section="stations-list" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('stations.list.subtitleLong')}
          </p>
        </div>
        <ExportXlsxButton
          filename="stations"
          sheetName="Stations"
          disabled={!allItems || allItems.length === 0}
          rows={() => (allItems ?? []).map(stationToExportRow)}
        />
      </header>

      {brandId ? (
        <TagFilter
          vocabulario={vocabulario}
          valor={filtro}
          onChange={setFiltro}
          resultado={allItems?.length ?? null}
        />
      ) : null}

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : !allItems || allItems.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title={t('stations.empty.title')}
          description={t('stations.list.emptyDesc')}
          action={
            <Link to="/projects">
              <Button>{t('stations.list.goToProjects')}</Button>
            </Link>
          }
        />
      ) : (
        <Card data-tour="stations-list">
          <CardHeader>
            <CardTitle>{t('stations.list.allStations')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left">{t('stations.column.name')}</th>
                  <th className="py-2 text-left">{t('stations.column.event')}</th>
                  <th className="py-2 text-left">{t('stations.list.column.survey')}</th>
                  <th className="py-2 text-left">{t('tags.filter.label')}</th>
                  <th className="py-2 text-left">{t('stations.list.column.allocated')}</th>
                  <th className="py-2 text-right">{t('stations.list.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--color-border-subtle)]">
                    <td className="py-3 font-semibold">{s.name}</td>
                    <td className="py-3 text-[var(--color-fg-muted)]">
                      {eventoDe[s.eventId]?.name ?? `#${s.eventId}`}
                    </td>
                    <td className="py-3 text-[var(--color-fg-muted)]">
                      {s.surveysId ? `#${s.surveysId}` : '—'}
                    </td>
                    <td className="py-3">
                      {brandId ? (
                        <EntityTags
                          companyId={brandId}
                          entityType="station"
                          entityId={s.id}
                          vocabulario={vocabulario}
                          valor={asignaciones[s.id] ?? []}
                        />
                      ) : null}
                    </td>
                    <td className="py-3 text-[var(--color-fg-muted)]">
                      {(() => {
                        const a = asignado[s.id];
                        const grupo = eventoDe[s.eventId]?.eventGroupId;
                        return (
                          <span className="inline-flex flex-col gap-0.5 leading-tight">
                            <span className={a ? 'tabular-nums text-[var(--color-fg-default)]' : ''}>
                              {a
                                ? t('stations.list.allocatedCell', { units: a.units, items: a.items })
                                : t('stations.list.allocatedNone')}
                            </span>
                            {grupo ? (
                              <Link
                                to={`/projects/${grupo}?tab=plan`}
                                className="text-xs text-[var(--color-accent-ink)] hover:underline"
                              >
                                {t('stations.list.allocatedLink')}
                              </Link>
                            ) : null}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Link to={`/stations/${s.id}`}>
                          <Button size="sm" variant="outline">
                            {t('stations.list.allocate')}
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          leftIcon={<QrCode className="h-4 w-4" />}
                          onClick={() => setQrStation({ id: s.id, name: s.name })}
                        >
                          {t('stations.list.qrShort')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...pager} noun={t('stations.title').toLowerCase()} />
          </CardContent>
        </Card>
      )}

      <StationQrDialog
        open={qrStation !== null}
        onOpenChange={(v) => !v && setQrStation(null)}
        stationId={qrStation?.id ?? 0}
        stationName={qrStation?.name}
      />
    </div>
  );
}

function stationToExportRow(s: XkStation): Record<string, unknown> {
  return {
    ID: s.id,
    Name: s.name,
    'Event ID': s.eventId,
    'Journey ID': s.surveysId ?? '',
    Status: s.status ?? '',
    'Created at': s.createdAt ?? '',
  };
}
